import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { formatNum } from "../lib/utils";
import { ProvenanceTrigger } from "../components/provenance/ProvenanceTrigger";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Database, Gauge, FileStack, AlertTriangle, GitBranch, Cpu, CheckCircle2, FileText, Sparkles, Plus, CalendarClock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { AddEsgDataDialog } from "../components/AddEsgDataDialog";
import { EsgRecordsBrowser } from "../components/EsgRecordsBrowser";
import { CostTrendsTab } from "../components/CostTrendsTab";
import { CompletenessTab } from "../components/CompletenessTab";
import { BaselineSeeding, SyntheticBanner } from "../components/BaselineSeeding";
import { ImportRunsList } from "../components/ImportRunsList";
import { ExportReconciliation } from "../components/ExportReconciliation";

const SOURCE_TONE = { "Manual Entry": "outline", "CSV / Bulk Upload": "lime", "Document Extraction": "warning", "Supplier": "success" };
const band = (p) => (p >= 70 ? "text-[#00E65B]" : p >= 45 ? "text-[#FFB800]" : "text-red-500");
const TYPE_ICON = { data_point: Database, document: FileText, ai: Sparkles };

function BaselineBanner() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/baseline").then((r) => setS(r.data.summary)).catch(() => {}); }, []);
  const has = s && s.count > 0;
  return (
    <div className="card-flat p-4 flex flex-wrap items-center gap-4 border-accent/30" data-testid="baseline-banner">
      <div className="h-10 w-10 rounded-sm bg-accent/10 border border-accent/20 grid place-items-center shrink-0"><CalendarClock className="h-5 w-5 text-accent" /></div>
      <div className="min-w-0 flex-1">
        <div className="font-display font-semibold text-sm">Baseline Year (GHG Protocol base year)</div>
        <div className="text-xs text-muted-foreground">
          {has
            ? <>Base year{s.base_years?.length > 1 ? "s" : ""} <span className="font-mono text-foreground">{(s.base_years || []).map((y) => `FY${y}`).join(", ")}</span> · {s.count} metric baselines · {s.locked_count} locked{s.env_reduction_pct != null && (s.env_reduction_pct >= 0
              ? <> · Env reduction <span className="text-[#00E65B] font-mono" data-testid="baseline-env-reduction">{s.env_reduction_pct}%</span> vs baseline</>
              : <> · Env change <span className="text-red-400 font-mono" data-testid="baseline-env-change">+{Math.abs(s.env_reduction_pct)}%</span> vs baseline</>)}</>
            : "Set the fixed reference year for your metrics so reductions are measured like-for-like."}
        </div>
      </div>
      <Link to="/baseline"><Button variant={has ? "outline" : "primary"} size="sm" data-testid="open-baseline-wizard-btn">{has ? "Manage baselines" : "Set baseline year"}</Button></Link>
    </div>
  );
}

function Stat({ label, value, unit, sub, icon: Icon, testid, tone, pkey }) {
  return (
    <div className="card-flat p-4 group" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon className="h-4 w-4" /><span className="label-eyebrow text-[10px]">{label}</span>{pkey && <ProvenanceTrigger pkey={pkey} className="ml-auto" />}</div>
      <div className={`font-mono text-2xl font-bold tracking-tight ${tone || ""}`}>{value}<span className="text-xs text-muted-foreground font-sans ml-1">{unit}</span></div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

const ORIGIN_LABEL = { measured: "Measured", calculated: "Calculated", estimate: "Estimate", declaration: "Declaration", ai_inferred: "AI-inferred" };
const ORIGIN_TONE = { measured: "success", calculated: "success", estimate: "warning", declaration: "outline", ai_inferred: "warning" };

function EvidenceOriginTab() {
  const [d, setD] = useState(null);
  const [pending, setPending] = useState(null);   // { category, origin, will_change, skipped_independent }
  const [lastEvent, setLastEvent] = useState(null); // { event_id, category, origin, updated }
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/data-management/evidence-origin").then((r) => setD(r.data)).catch(() => setD({ total: 0, categories: [], by_origin: {}, by_value_state: {}, origins: [] }));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-muted-foreground font-mono text-sm p-4">Loading evidence origin…</div>;

  // Safe reclassification — ALWAYS preview first (counts + skipped), confirm explicitly, undoable.
  const preview = async (category, origin) => {
    try {
      const r = await api.post("/data-management/evidence-origin/reclassify", { category, origin, confirm: false });
      setPending({ category, origin, ...r.data });
    } catch { toast.error("Could not preview reclassification"); }
  };
  const confirmReclassify = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const r = await api.post("/data-management/evidence-origin/reclassify",
        { category: pending.category, origin: pending.origin, confirm: true });
      toast.success(`${r.data.updated} record(s) set to ${ORIGIN_LABEL[pending.origin]} · ${r.data.skipped_independent} left unchanged`);
      setLastEvent({ ...r.data, origin: pending.origin, category: pending.category });
      setPending(null); load();
    } catch { toast.error("Reclassification failed"); }
    finally { setBusy(false); }
  };
  const undo = async () => {
    if (!lastEvent) return;
    setBusy(true);
    try {
      const r = await api.post("/data-management/evidence-origin/undo", { event_id: lastEvent.event_id });
      toast.success(`Undone — ${r.data.restored} record(s) restored`);
      setLastEvent(null); load();
    } catch { toast.error("Undo failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="dm-evidence-origin">
      <div className="card-flat p-4">
        <div className="label-eyebrow mb-2">Origin qualification — how each value was produced</div>
        <p className="text-[11px] text-muted-foreground mb-3">Score Intelligence reads these origins directly: measured &amp; calculated carry more confidence than estimate, declaration or AI-inferred. <span className="font-mono">{d.documented_pct}%</span> of quantifiable records are evidence-backed (measured/calculated).</p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-[11px]" data-testid="dm-origin-reconciliation">
          <span><span className="label-eyebrow text-[10px]">ESG records (all pillars):</span> <span className="font-mono font-bold text-sm" data-testid="dm-origin-total">{d.total}</span></span>
          <span className="text-muted-foreground">= <span className="font-mono" data-testid="dm-origin-quantifiable">{d.quantifiable_total ?? d.total}</span> quantifiable + <span className="font-mono" data-testid="dm-origin-reference">{d.reference_only || 0}</span> reference-only (no value)</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {(d.origins || Object.keys(d.by_origin)).map((o) => (
            <div key={o} className="card-flat p-3" data-testid={`dm-origin-${o}`}>
              <Badge variant={ORIGIN_TONE[o] || "outline"} className="text-[9px] mb-1">{ORIGIN_LABEL[o] || o}</Badge>
              <div className="font-mono text-xl font-bold">{d.by_origin[o] || 0}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-3 text-[11px] text-muted-foreground">
          <span className="label-eyebrow text-[10px]">Value states:</span>
          {Object.entries(d.by_value_state || {}).map(([k, v]) => <span key={k} className="font-mono">{k}: {v}</span>)}
        </div>
      </div>

      {lastEvent && (
        <div className="card-flat p-3 border-accent/40 flex items-center justify-between gap-3" data-testid="dm-origin-undo-bar">
          <div className="text-[11px] text-muted-foreground">
            Reclassified <span className="font-mono font-semibold text-foreground">{lastEvent.updated}</span> “{lastEvent.category}” record(s) to <span className="font-semibold text-foreground">{ORIGIN_LABEL[lastEvent.origin]}</span>.
          </div>
          <Button variant="outline" size="sm" onClick={undo} disabled={busy} data-testid="dm-origin-undo-btn">Undo</Button>
        </div>
      )}

      <div className="card-flat p-4">
        <div className="label-eyebrow mb-2">Reclassify by category</div>
        <p className="text-[11px] text-muted-foreground mb-2">Choose a target origin — you’ll see how many records change before anything is written. Measured / bill-sourced / already-corrected records keep their own origin and are skipped.</p>
        <div className="space-y-1.5">
          {d.categories.map((c) => (
            <div key={c.category} className="flex items-center justify-between gap-3 border-b border-border py-1.5" data-testid={`dm-origin-cat-${c.category}`}>
              <div className="min-w-0"><div className="text-sm truncate">{c.category}</div><div className="text-[10px] text-muted-foreground">{c.total} records{c.reference_only ? ` · ${c.reference_only} reference-only` : ""} · dominant <Badge variant={ORIGIN_TONE[c.dominant] || "outline"} className="text-[8px]">{ORIGIN_LABEL[c.dominant] || c.dominant}</Badge></div></div>
              <Select value="" onValueChange={(v) => preview(c.category, v)}>
                <SelectTrigger className="w-44 h-8 text-xs shrink-0" data-testid={`dm-origin-select-${c.category}`}><SelectValue placeholder="Reclassify to…" /></SelectTrigger>
                <SelectContent>{(d.origins || []).map((o) => <SelectItem key={o} value={o}>{ORIGIN_LABEL[o]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
          {d.categories.length === 0 && <div className="text-sm text-muted-foreground">No data points yet.</div>}
        </div>
      </div>

      <Dialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <DialogContent data-testid="dm-origin-confirm-dialog">
          <DialogHeader><DialogTitle>Confirm evidence-origin reclassification</DialogTitle></DialogHeader>
          {pending && (
            <div className="space-y-2 text-sm">
              <p>
                This will set <span className="font-mono font-semibold" data-testid="dm-origin-confirm-count">{pending.will_change}</span> “{pending.category}” record(s) to <span className="font-semibold">{ORIGIN_LABEL[pending.origin]}</span>.
              </p>
              <p className="text-[12px] text-muted-foreground" data-testid="dm-origin-confirm-skipped">
                <span className="font-mono font-semibold text-foreground">{pending.skipped_independent}</span> record(s) with their own evidence origin (measured / bill-sourced / already corrected) will be left unchanged.
              </p>
              <p className="text-[11px] text-muted-foreground">This is recorded as a single change-history event and can be undone.</p>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setPending(null)} data-testid="dm-origin-confirm-cancel">Cancel</Button>
            <Button onClick={confirmReclassify} disabled={busy || !pending?.will_change} data-testid="dm-origin-confirm-apply">
              {pending?.will_change ? `Reclassify ${pending.will_change}` : "Nothing to change"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function DataManagement() {
  const [d, setD] = useState(null);
  const [log, setLog] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [params] = useSearchParams();
  const initialTab = params.get("tab") === "baseline" ? "baseline" : "records";

  const load = () => {
    api.get("/data-management/dashboard").then((r) => setD(r.data)).catch(() => setD({ empty: true }));
    api.get("/data-management/audit-log").then((r) => setLog(r.data.events)).catch(() => {});
    setReloadKey((k) => k + 1);
  };
  useEffect(() => { load(); }, []);

  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading data management…</div>;
  if (d.empty) return (
    <div className="p-8 space-y-4">
      <div className="flex justify-end"><Button variant="primary" onClick={() => setAddOpen(true)} data-testid="add-esg-data-btn"><Plus className="h-4 w-4" /> Add ESG Data</Button></div>
      <div className="card-flat p-12 text-center"><h2 className="font-display text-2xl font-bold">No data yet.</h2><p className="text-muted-foreground mt-2">Capture your first ESG data point across Environment, Social or Governance.</p></div>
      <AddEsgDataDialog open={addOpen} onClose={() => setAddOpen(false)} onSaved={load} />
    </div>
  );

  const dp = d.data_points, dq = d.data_quality;
  const totalLineage = d.lineage.reduce((a, l) => a + l.count, 0) || 1;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="data-management-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow mb-1">Data Management</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3"><Database className="h-8 w-8 text-accent" /> Data Management</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Central hub for collecting, validating, reconciling and auditing ESG data — quality, evidence, anomalies, data lineage and AI processing logs.</p>
        </div>
        <Button variant="primary" onClick={() => setAddOpen(true)} data-testid="add-esg-data-btn"><Plus className="h-4 w-4" /> Add ESG Data</Button>
      </div>

      {/* Baseline CTA */}
      <BaselineBanner />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Data quality score" value={dq.score} unit="" icon={Gauge} testid="dm-stat-dq" pkey="dm.quality" tone={band(dq.score)} />
        <Stat label="ESG records (all pillars)" value={formatNum(dp.total)} unit="" icon={Database} testid="dm-stat-dp" pkey="dm.records"
          sub={`${dp.verified} verified${dp.unverified ? ` · ${dp.unverified} unverified` : ""} · ${formatNum(dp.environment ?? 0)} Env / ${formatNum(dp.social_governance ?? 0)} S&G${dp.consistency_ok === false ? " · ⚠ tile/export mismatch — check pillar tags" : " · matches CSV export"}`} />
        <Stat label="Evidence files" value={d.evidence.documents} unit="" icon={FileStack} testid="dm-stat-evidence" pkey="dm.evidence" sub={`${d.evidence.pending_review} pending`} />
        <Stat label="Open anomalies" value={d.anomalies.open + (d.anomalies.escalated || 0)} unit="" icon={AlertTriangle} testid="dm-stat-anomalies" pkey="dm.anomalies" tone={(d.anomalies.open + (d.anomalies.escalated || 0)) ? "text-[#FFB800]" : ""} sub={`${d.anomalies.escalated || 0} escalated · ${d.anomalies.resolved} resolved`} />
        <Stat label="Reconciliation findings" value={d.reconciliation.findings} unit="" icon={GitBranch} testid="dm-stat-reconciliation" pkey="dm.reconciliation" />
        <Stat label="AI processing" value={formatNum(d.ai_processing.total_calls)} unit="calls" icon={Cpu} testid="dm-stat-ai" pkey="dm.ai" />
      </div>

      <SyntheticBanner />
      <Tabs defaultValue={initialTab}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="records" data-testid="dm-tab-records">ESG Records</TabsTrigger>
          <TabsTrigger value="cost-trends" data-testid="dm-tab-cost-trends">Cost Trends</TabsTrigger>
          <TabsTrigger value="completeness" data-testid="dm-tab-completeness">Completeness</TabsTrigger>
          <TabsTrigger value="baseline" data-testid="dm-tab-baseline">Baseline Seeding</TabsTrigger>
          <TabsTrigger value="import-runs" data-testid="dm-tab-import-runs">Import Runs</TabsTrigger>
          <TabsTrigger value="reconciliation" data-testid="dm-tab-reconciliation">Export Reconciliation</TabsTrigger>
          <TabsTrigger value="overview" data-testid="dm-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="lineage" data-testid="dm-tab-lineage">Data Lineage</TabsTrigger>
          <TabsTrigger value="origin" data-testid="dm-tab-origin">Evidence Origin</TabsTrigger>
          <TabsTrigger value="audit" data-testid="dm-tab-audit">Audit & Processing Log</TabsTrigger>
        </TabsList>

        <TabsContent value="baseline" className="mt-4">
          <BaselineSeeding />
        </TabsContent>

        <TabsContent value="records" className="mt-4">
          <EsgRecordsBrowser reloadKey={reloadKey} />
        </TabsContent>

        <TabsContent value="cost-trends" className="mt-4">
          <CostTrendsTab />
        </TabsContent>

        <TabsContent value="completeness" className="mt-4">
          <CompletenessTab />
        </TabsContent>

        <TabsContent value="import-runs" className="mt-4">
          <ImportRunsList reloadKey={reloadKey} />
        </TabsContent>

        <TabsContent value="reconciliation" className="mt-4">
          <ExportReconciliation />
        </TabsContent>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="card-flat p-4 space-y-3" title={dq.methodology}>
              <div className="label-eyebrow mb-1">Data quality components</div>
              {[["Completeness", dq.completeness_pct], ["Verification", dq.verification_pct], ["Anomaly resolution", dq.resolution_pct]].map(([l, v]) => (
                <div key={l}><div className="flex justify-between text-xs mb-1"><span>{l}</span><span className={`font-mono ${band(v)}`}>{v}%</span></div><Progress value={v} className="h-1.5" /></div>
              ))}
              <div className="flex justify-between text-xs pt-1 border-t border-border"><span className="text-muted-foreground">Distinct months covered</span><span className="font-mono">{dq.months_covered}</span></div>
            </div>
            <div className="card-flat p-4">
              <div className="label-eyebrow mb-2">Submissions by category</div>
              <div className="space-y-1.5">
                {dp.top_categories.map((c) => (
                  <div key={c.category}><div className="flex justify-between text-xs mb-0.5"><span className="truncate max-w-[220px]">{c.category}</span><span className="font-mono">{c.count}</span></div>
                    <Progress value={(c.count / dp.total) * 100} className="h-1" /></div>
                ))}
              </div>
              {dp.count_basis && <p className="text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border" data-testid="dm-count-basis">{dp.count_basis}</p>}
            </div>
          </div>
          {d.facility_completeness?.rows?.length > 0 && (
            <div className="card-flat p-4" data-testid="facility-completeness-card">
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow">Facility completeness — FY{d.facility_completeness.year}</div>
                {d.facility_completeness.gaps > 0
                  ? <Badge variant="warning" data-testid="facility-gaps-badge">{d.facility_completeness.gaps} sparse source{d.facility_completeness.gaps > 1 ? "s" : ""}</Badge>
                  : <Badge variant="success" data-testid="facility-gaps-badge">All sources complete</Badge>}
              </div>
              <div className="space-y-1.5">
                {d.facility_completeness.rows.map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-xs" data-testid={`facility-row-${i}`}>
                    <span className="truncate max-w-[300px]">{r.facility} · <span className="text-muted-foreground">{r.category}</span></span>
                    <span className={`font-mono ${r.complete ? "text-[#00E65B]" : "text-[#FFB800]"}`}>{r.months_covered}/{r.expected_months} months · {r.records} rec</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">Sparse sources aren't necessarily wrong (e.g. quarterly travel logs) — but confirm any single-month facility before disclosure.</p>
            </div>
          )}
          <div className="grid grid-cols-4 gap-3">
            <Stat label="Verified" value={dp.verified} unit="" icon={CheckCircle2} tone="text-[#00E65B]" />
            <Stat label="Unverified" value={dp.unverified ?? 0} unit="" icon={CalendarClock} testid="dm-stat-unverified" tone={dp.unverified ? "text-[#FFB800]" : ""} />
            <Stat label="Flagged" value={dp.flagged} unit="" icon={AlertTriangle} tone={dp.flagged ? "text-[#FFB800]" : ""} />
            <Stat label="Scope 1/2/3" value={`${dp.by_scope.scope1}/${dp.by_scope.scope2}/${dp.by_scope.scope3}`} unit="" icon={Database} />
          </div>
        </TabsContent>

        <TabsContent value="lineage" className="mt-4">
          <div className="card-flat p-4">
            <div className="label-eyebrow mb-3 inline-flex items-center gap-1"><GitBranch className="h-3.5 w-3.5" /> Data lineage by source</div>
            <div className="space-y-3">
              {d.lineage.map((l) => (
                <div key={l.source} data-testid={`dm-lineage-${l.source}`}>
                  <div className="flex justify-between text-sm mb-1"><span className="inline-flex items-center gap-2"><Badge variant={SOURCE_TONE[l.source] || "outline"} className="text-[10px]">{l.source}</Badge></span><span className="font-mono text-xs">{l.count} · {Math.round((l.count / totalLineage) * 100)}%</span></div>
                  <Progress value={(l.count / totalLineage) * 100} className="h-2" />
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">Every data point is traceable to its origin — manual entry, bulk upload, document extraction or supplier submission — supporting audit-grade lineage.</p>
          </div>
        </TabsContent>

        <TabsContent value="origin" className="mt-4">
          <EvidenceOriginTab />
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <div className="card-flat overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Activity</TableHead><TableHead>Source</TableHead><TableHead>Status</TableHead><TableHead>By</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
              <TableBody>
                {log.map((e, i) => {
                  const TI = TYPE_ICON[e.type] || Database;
                  return (
                    <TableRow key={i} data-testid={`dm-audit-row-${i}`}>
                      <TableCell><TI className="h-4 w-4 text-muted-foreground" /></TableCell>
                      <TableCell className="text-sm">{e.action}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{e.source}</TableCell>
                      <TableCell><Badge variant={e.status === "verified" || e.status === "success" ? "success" : e.status === "flagged" || e.status === "fallback" ? "warning" : "outline"} className="text-[9px]">{e.status}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{e.by || "—"}</TableCell>
                      <TableCell className="text-[10px] font-mono text-muted-foreground">{e.at ? new Date(e.at).toLocaleDateString() : "—"}</TableCell>
                    </TableRow>
                  );
                })}
                {!log.length && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground text-sm">No recent activity.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <AddEsgDataDialog open={addOpen} onClose={() => setAddOpen(false)} onSaved={load} />
    </div>
  );
}
